// ─────────────────────────────────────────────────────────────────────────────
// DraggableArrows — THE arrow overlay for every target face. Extracted from
// components/Target.jsx (#2063) so all 17 faces share one implementation.
//
// ⚠ FIXED STYLES ONLY. Every glyph in this module — impact dot, stem, callout
// card, drag preview, crosshair — is styled by CSS class or by a hardcoded
// design token that does NOT depend on the arrow's score. This mirrors the
// carve-out documented on round-detail/svgDots.GroupEllipse: overlay glyphs
// must never take a score-derived hue. Making them score-derived was reported
// as a rendering bug three times (#1844 → #1848 → #1849) because faces with
// identical rules then rendered differently. `Target.faces.test.jsx` locks
// this: the overlay markup must be byte-identical for every score.
// ─────────────────────────────────────────────────────────────────────────────
import { clamp } from "../../hooks/useTargetInteraction";
import { previewRings } from "./FaceLayers";
import { toVbX, toVbY } from "../../lib/targetGeometry";

// ─────────────────────────────────────────────────────────────────────────────
// ArrowMark — a designed arrow marker: the precise impact point, a stem, and
// a numbered callout card. The card currently carries just the placement
// number but is sized to take additional per-arrow data labels (arrow /
// equipment spec) in a later milestone. While `lifted` it rises clear of the
// finger so the impact point stays visible, and it doubles as the drag grip.
//
// Card positions are resolved by solveCalloutPositions (see DraggableArrows)
// so a tight group of arrows never has overlapping cards.
// ─────────────────────────────────────────────────────────────────────────────
const CALLOUT_W = 24;    // callout card size — held a touch larger than the
const CALLOUT_H = 17;    // number needs, leaving room for future data labels
const CALLOUT_RISE = 16; // card starts this far above the impact point, idle
const CALLOUT_LIFT = 36; // …and this far while lifted, clearing a fingertip

// Direction to push along an axis; falls back to the sign of `tie` when the
// two items sit exactly on the axis, so a deterministic split still happens.
function _pushSign(delta, tie) {
  if (delta > 0.01) return 1;
  if (delta < -0.01) return -1;
  return tie >= 0 ? 1 : -1;
}

/**
 * Resolve a non-overlapping callout position for every arrow.
 *
 * Each card starts directly above its impact point; a short relaxation then
 * pushes cards apart from one another and clear of every arrow's impact
 * point. Returns the card centres in viewbox units, clamped on-canvas.
 * `arrows` carry normalised x/y; the lifted arrow's card starts higher.
 */
export function solveCalloutPositions(arrows, liftedIndex) {
  const impacts = arrows.map((a) => ({
    x: toVbX(a.x),
    y: toVbY(a.y),
  }));
  const labels = impacts.map((p, i) => ({
    x: p.x,
    y: p.y - (i === liftedIndex ? CALLOUT_LIFT : CALLOUT_RISE),
  }));
  // While a drag is in progress the lifted card must stay at a fixed, stable
  // offset above the contact point — that steady chip above the finger is the
  // visual "preview" of the landing position. Running the solver here would
  // re-push the card on every pointer-move, causing jitter and losing the
  // preview. Other callouts also hold still so nothing jumps during drag.
  if (liftedIndex !== null) return labels;

  const CARD_GAP = 5;    // clear gap kept between two callout cards
  const POINT_CLEAR = 6; // clear gap kept between a card and an impact point
  const minX = CALLOUT_W / 2 + 2;
  const minY = CALLOUT_H / 2 + 2;

  for (let pass = 0; pass < 60; pass++) {
    for (let i = 0; i < labels.length; i++) {
      let fx = 0;
      let fy = 0;
      // push apart from every other callout card (axis-aligned overlap)
      for (let j = 0; j < labels.length; j++) {
        if (j === i) continue;
        const dx = labels[i].x - labels[j].x;
        const dy = labels[i].y - labels[j].y;
        const ox = CALLOUT_W + CARD_GAP - Math.abs(dx);
        const oy = CALLOUT_H + CARD_GAP - Math.abs(dy);
        if (ox > 0 && oy > 0) {
          if (ox <= oy) fx += (_pushSign(dx, i - j) * ox) / 2;
          else fy += (_pushSign(dy, i - j) * oy) / 2;
        }
      }
      // push clear of every arrow impact point
      for (let k = 0; k < impacts.length; k++) {
        const dx = labels[i].x - impacts[k].x;
        const dy = labels[i].y - impacts[k].y;
        const ox = CALLOUT_W / 2 + POINT_CLEAR - Math.abs(dx);
        const oy = CALLOUT_H / 2 + POINT_CLEAR - Math.abs(dy);
        if (ox > 0 && oy > 0) {
          if (ox <= oy) fx += _pushSign(dx, 1) * ox;
          else fy += _pushSign(dy, -1) * oy;
        }
      }
      labels[i].x = clamp(labels[i].x + fx, minX, 240 - minX);
      labels[i].y = clamp(labels[i].y + fy, minY, 240 - minY);
    }
  }
  return labels;
}

// ── Drag preview ─────────────────────────────────────────────────────────────
// A face-accurate ~2× magnified view of the ring area that floats above the
// contact point while an arrow is being dragged, letting the archer see
// exactly which ring they're landing on without their finger in the way.
// ─────────────────────────────────────────────────────────────────────────────
const PREVIEW_SZ = 57;   // preview window size in viewbox units (~75% of original)
const PREVIEW_ZR = 18;   // half-width of the zoomed area (2.1× magnification)

// Gap from the arrow impact point to the near edge of the preview window.
const PREVIEW_V_GAP = 36;

/**
 * Continuously orbit the preview around the arrow at a fixed radius R.
 *
 * The orbital angle φ starts at 0 (straight above) and rotates smoothly as
 * the arrow approaches the top of the canvas — naturally flowing sideways and,
 * if needed, past horizontal — with no discrete mode switches. Dragging back
 * down reverses the arc continuously.
 *
 * φ is derived from the constraint that the preview's top edge stays at PAD
 * when possible:  R·cos(φ) = cy − ph/2 − PAD
 */
function previewPos(cx, cy) {
  const pw = PREVIEW_SZ;
  const ph = PREVIEW_SZ;
  const PAD = 4;
  const R = PREVIEW_V_GAP + ph / 2;  // orbital radius (arrow → preview centre)

  // Compute the orbital angle. When there is room above (cy large), φ = 0
  // and the preview sits directly above. As cy shrinks, φ increases, sweeping
  // the preview sideways and eventually below if cy is very small.
  const phi = Math.acos(clamp((cy - ph / 2 - PAD) / R, -1, 1));

  // Always swing left. If the preview reaches the left edge it stops there
  // and lets the finger move past underneath.
  const pcx = cx - R * Math.sin(phi);
  const pcy = cy - R * Math.cos(phi);

  return {
    px: clamp(pcx - pw / 2, PAD, 240 - pw - PAD),
    py: clamp(pcy - ph / 2, PAD, 240 - ph - PAD),
  };
}

function DragPreview({ face, cx, cy, num, otherArrows = [] }) {
  const { px, py } = previewPos(cx, cy);
  const pw = PREVIEW_SZ;
  const ph = PREVIEW_SZ;

  // SVG x/y attributes are in viewbox units — the preview tracks the finger
  // frame-perfect with no lag. No CSS transform, no transition.
  return (
    <g className="drag-preview">
      <rect className="dp-bg" x={px} y={py} width={pw} height={ph} rx="8" />
      <svg
        x={px} y={py}
        width={pw} height={ph}
        viewBox={`${cx - PREVIEW_ZR} ${cy - PREVIEW_ZR} ${PREVIEW_ZR * 2} ${PREVIEW_ZR * 2}`}
        overflow="hidden"
      >
        {previewRings(face)}
        {/* Other placed arrows — small dots so you can see where they sit.
            FIXED styling: never score-derived (see the module header). */}
        {otherArrows.map((a, i) => {
          if (a.x == null || a.y == null) return null;
          const ax = toVbX(a.x);
          const ay = toVbY(a.y);
          return <circle key={i} cx={ax} cy={ay} r="2.2" fill="var(--vellum)" stroke="var(--nock)" strokeWidth=".6" opacity=".85" />;
        })}
        {/* Crosshair at the exact impact point */}
        <circle cx={cx} cy={cy} r="1.5" fill="var(--ring-dark)" opacity=".9" />
        <line x1={cx - 5} y1={cy} x2={cx + 5} y2={cy} stroke="var(--nock)" strokeWidth=".8" opacity=".55" />
        <line x1={cx} y1={cy - 5} x2={cx} y2={cy + 5} stroke="var(--nock)" strokeWidth=".8" opacity=".55" />
      </svg>
      <rect className="dp-frame" x={px} y={py} width={pw} height={ph} rx="8" />
      <text className="dp-num" x={px + pw - 6} y={py + 6} textAnchor="end" dominantBaseline="hanging">
        {num}
      </text>
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ArrowMark — impact dot + callout card (idle) or magnified ring preview
// (while dragging). Recedes to just the dot when another arrow is being
// dragged, clearing the visual field.
// ─────────────────────────────────────────────────────────────────────────────
function ArrowMark({ a, index, showArrowNums, lifted = false, recede = false, label, face = "wa40", otherArrows, onPointerDown }) {
  const cx = toVbX(a.x);
  const cy = toVbY(a.y);
  const lx = label?.x ?? cx;
  const ly = label?.y ?? cy - CALLOUT_RISE;
  const num = a.n || index + 1;
  const draggable = !!onPointerDown;

  return (
    <g
      className={"arrow-mark" + (lifted ? " arrow-mark--lifted" : recede ? " arrow-mark--recede" : "")}
      onPointerDown={onPointerDown}
      style={draggable ? { cursor: lifted ? "grabbing" : "grab" } : undefined}
    >
      {/* transparent hit area — sized for a fingertip */}
      {draggable && <circle cx={cx} cy={cy} r="13" fill="transparent" />}
      {/* impact dot — always visible. Styled by .am-point in CSS; NEVER by
          the arrow's score. */}
      <circle className="am-point" cx={cx} cy={cy} r={lifted ? 3.4 : 2.6} />
      {lifted ? (
        // Magnified ring preview replaces the callout card during drag.
        <DragPreview face={face} cx={cx} cy={cy} num={num} otherArrows={otherArrows} />
      ) : (
        // Idle: stem + callout card (hides via CSS when receding).
        <>
          <line className="am-stem" x1={cx} y1={cy} x2={lx} y2={ly} />
          <g
            className="am-label"
            style={{ transformOrigin: `${lx}px ${ly}px` }}
          >
            <rect
              className="am-callout"
              x={lx - CALLOUT_W / 2}
              y={ly - CALLOUT_H / 2}
              width={CALLOUT_W}
              height={CALLOUT_H}
              rx="4"
            />
            {showArrowNums && (
              <text className="am-num" x={lx} y={ly + 0.5} textAnchor="middle" dominantBaseline="central">
                {num}
              </text>
            )}
          </g>
        </>
      )}
    </g>
  );
}

/** Renders the placement-order arrow markers; draggable when `draggable`. */
export function DraggableArrows({
  arrows,
  showArrowNums = true,
  draggable,
  liftedIndex = null,
  face = "wa40",
  onStartDrag,
}) {
  const dragging = liftedIndex !== null;
  const labels = solveCalloutPositions(arrows, liftedIndex);
  // Draw the lifted arrow last so it floats above its neighbours.
  const order = arrows.map((_, i) => i);
  order.sort((a, b) => (a === liftedIndex ? 1 : 0) - (b === liftedIndex ? 1 : 0));
  return order.map((i) => (
    <ArrowMark
      key={i}
      a={arrows[i]}
      index={i}
      label={labels[i]}
      face={face}
      showArrowNums={showArrowNums}
      lifted={i === liftedIndex}
      recede={dragging && i !== liftedIndex}
      otherArrows={i === liftedIndex ? arrows.filter((_, j) => j !== i) : undefined}
      onPointerDown={draggable ? (e) => onStartDrag(i, e) : undefined}
    />
  ));
}
